<script setup lang="ts">
/**
 * PasswordReveal — the show/hide eye inside a password box (Aran, 2026-10-17).
 *
 * Wraps the caller's own <input> through a scoped slot, so the input keeps the
 * caller's scoped styles and v-model; this component owns only whether it is
 * shown. Usage:
 *
 *   <PasswordReveal v-slot="{ type }">
 *     <input v-model="pw" :type="type" autocomplete="current-password" />
 *   </PasswordReveal>
 *
 * Hidden is the default, and the state is local: when the box closes the
 * component unmounts and the next open starts hidden again.
 */
import { computed, ref } from 'vue'
import { useI18n } from '@/composables/useI18n'

const { t } = useI18n()
const shown = ref(false)
const type = computed(() => (shown.value ? 'text' : 'password'))
const label = computed(() => (shown.value ? t('auth.hidePassword') : t('auth.showPassword')))
</script>

<template>
  <div class="pw-reveal">
    <slot :type="type" :shown="shown" />
    <button
      type="button"
      class="pw-reveal-btn"
      data-testid="password-reveal"
      :aria-label="label"
      :aria-pressed="shown ? 'true' : 'false'"
      :title="label"
      @click="shown = !shown"
      @mousedown.prevent
    >
      <!-- Eye when hidden (tap to show); eye struck through when shown. -->
      <svg v-if="!shown" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19"/>
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
        <line x1="2" y1="2" x2="22" y2="22"/>
      </svg>
    </button>
  </div>
</template>

<style scoped>
.pw-reveal {
  position: relative;
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
}

/* Doubled class so this out-ranks the caller's own `.wrapper input` padding:
   the text must never run under the eye. */
.pw-reveal.pw-reveal :slotted(input) {
  flex: 1;
  width: 100%;
  min-width: 0;
  padding-right: 48px;
}

.pw-reveal-btn {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #7a736c;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.pw-reveal-btn:hover {
  color: #2c2622;
}

.pw-reveal-btn:focus-visible {
  outline: 2px solid #2c2622;
  outline-offset: -4px;
}

.pw-reveal-btn svg {
  width: 20px;
  height: 20px;
}
</style>
